import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'antd';
import Question from '../../Icons/Question';

// The browser sizes the bubble before balancing the lines, so a balanced text
// leaves empty space on the right. Shrink the text to its widest line instead.
// Measured while the open animation may still scale the bubble, hence the scale factor.
const fitToWidestLine = (el) => {
  el.style.width = '';
  const scale = el.getBoundingClientRect().width / el.offsetWidth || 1;
  const range = document.createRange();
  range.selectNodeContents(el);
  const widest = Math.max(0, ...[...range.getClientRects()].map((r) => r.width));
  if (widest > 0) el.style.width = `${Math.ceil(widest / scale)}px`;
};

// antd aligns the bubble before it shrinks and pushes it flush against the screen
// edge when it does not fit (e.g. on mobile), leaving the arrow behind. Center the
// bubble over the icon instead, keep 16px to the screen edges and put the arrow on the icon.
const EDGE = 16;
const placeOverIcon = (el, icon) => {
  const popup = el.closest('.ant-tooltip');
  const arrow = popup && popup.querySelector('.ant-tooltip-arrow');
  if (!arrow || !icon) return;
  const width = popup.offsetWidth;
  const viewport = document.documentElement.clientWidth;
  const iconRect = icon.getBoundingClientRect();
  const iconCenter = iconRect.left + iconRect.width / 2;
  const left = Math.min(Math.max(iconCenter - width / 2, EDGE), viewport - EDGE - width);
  const parentLeft = popup.offsetParent ? popup.offsetParent.getBoundingClientRect().left : 0;
  const popupLeft = `${Math.round(left - parentLeft)}px`;
  if (popup.style.left !== popupLeft) popup.style.left = popupLeft;
  // antd also shifts the arrow with a CSS transform (translateX(-50%) for "top")
  const transform = window.getComputedStyle(arrow).transform;
  const shiftX = transform && transform !== 'none' ? new DOMMatrix(transform).m41 : 0;
  const arrowLeft = `${Math.round(iconCenter - left - arrow.offsetWidth / 2 - shiftX)}px`;
  if (arrow.style.left !== arrowLeft) arrow.style.left = arrowLeft;
  arrow.style.right = 'auto';
};

// "?" icon with a green tooltip, used next to checkout headings.
// The bubble fits its text up to 420px, lines are balanced and centered.
const InfoTooltip = ({ text }) => {
  const textRef = useRef(null);
  const iconRef = useRef(null);

  const observerRef = useRef(null);
  useEffect(() => () => observerRef.current && observerRef.current.disconnect(), []);

  // antd re-aligns the bubble several times while and after it opens (and ignores
  // onPopupAlign). Fit the text once per opening, then put the bubble back on the
  // icon whenever antd moves it. The observer runs before the next paint, so nothing flickers.
  const handleOpenChange = (open) => {
    if (!open) return;
    let tries = 0;
    const attach = () => {
      const el = textRef.current;
      const popup = el && el.closest('.ant-tooltip');
      if (!popup) {
        tries += 1;
        if (tries < 30) requestAnimationFrame(attach);
        return;
      }
      fitToWidestLine(el);
      placeOverIcon(el, iconRef.current);
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new MutationObserver(() => placeOverIcon(el, iconRef.current));
      observerRef.current.observe(popup, { attributes: true, attributeFilter: ['style', 'class'] });
    };
    requestAnimationFrame(attach);
  };

  return (
    <Tooltip
      title={
        <span
          ref={textRef}
          style={{
            color: '#fff',
            display: 'block',
            margin: '0 auto',
            textAlign: 'center',
            lineHeight: 1.5,
            textWrap: 'balance',
          }}
        >
          {text}
        </span>
      }
      color="#95bd51"
      overlayStyle={{ maxWidth: 'min(420px, calc(100vw - 32px))' }}
      onOpenChange={handleOpenChange}
    >
      <div style={{ cursor: 'pointer', marginTop: 5 }}>
        <span ref={iconRef} style={{ display: 'inline-flex' }}>
          <Question />
        </span>
      </div>
    </Tooltip>
  );
};

InfoTooltip.propTypes = {
  text: PropTypes.node.isRequired,
};

export default InfoTooltip;
